import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Popconfirm, Tag, Input } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FileOutlined, ReloadOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ProForm, ProFormText, ProFormDigit, ProFormSelect, ModalForm } from '@ant-design/pro-components';

interface Invoice {
  id: number;
  company_name: string;
  credit_code: string;
  price: number;
  contact_name: string;
  contact_phone: string;
  address: string;
  invoice_status: number;
  status_flag: number;
  create_time: string;
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = ProForm.useForm();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/invoices', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setInvoices(resp.data || []);
      } else {
        message.error(resp?.error || '获取发票列表失败');
      }
    } catch (err) {
      message.error('获取发票列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingInvoice(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Invoice) => {
    setEditingInvoice(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/invoices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchInvoices();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingInvoice 
        ? `/api/admin/invoices/${editingInvoice.id}`
        : '/api/admin/invoices';
      const method = editingInvoice ? 'PUT' : 'POST';

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingInvoice ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchInvoices();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err) {
      message.error('操作失败');
    }
  };

  // 过滤数据
  const filteredInvoices = invoices.filter(i => 
    i.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    i.contact_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    i.contact_phone?.includes(searchKeyword) ||
    i.credit_code?.includes(searchKeyword)
  );

  // 统计数据
  const totalAmount = invoices.reduce((sum, i) => sum + (i.price || 0), 0);
  const invoicedCount = invoices.filter(i => i.invoice_status === 1).length;
  const pendingCount = invoices.filter(i => i.invoice_status === 0).length;

  const columns: ColumnsType<Invoice> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '纳税人识别号',
      dataIndex: 'credit_code',
      key: 'credit_code',
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span>
    },
    {
      title: '开票金额',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => (
        <span style={{ color: '#f59e0b', fontWeight: 600 }}>
          ¥{price?.toFixed(2) || '0.00'}
        </span>
      ),
    },
    {
      title: '联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
    },
    {
      title: '联系电话',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>
    },
    {
      title: '邮寄地址',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
    },
    {
      title: '开票状态',
      dataIndex: 'invoice_status',
      key: 'invoice_status',
      render: (status: number) => {
        const statusMap: Record<number, { text: string; color: string; icon: any }> = {
          0: { text: '未开票', color: 'warning', icon: null },
          1: { text: '已开票', color: 'success', icon: null },
        };
        const s = statusMap[status] || statusMap[0];
        return <Tag color={s.color} style={{ borderRadius: 12 }}>{s.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#3b82f6' }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个发票吗？"
            description="删除后将无法恢复"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)' }}>
            <PrinterOutlined />
          </div>
          <div>
            <h2>发票管理</h2>
            <p>管理所有发票信息</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索发票..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchInvoices}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
            }}
          >
            新增发票
          </Button>
        </Space>
      </div>
      
      {/* 统计卡片 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, 1fr)', 
        gap: 16, 
        padding: '0 32px',
        marginBottom: 24
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
          borderRadius: 12,
          padding: 20,
          color: 'white'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>总开票金额</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>¥{totalAmount.toFixed(2)}</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #10b981, #059669)',
          borderRadius: 12,
          padding: 20,
          color: 'white'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>已开票</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{invoicedCount}</div>
        </div>
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          borderRadius: 12,
          padding: 20,
          color: 'white'
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>待开票</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{pendingCount}</div>
        </div>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{filteredInvoices.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredInvoices}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      {/* 编辑弹窗 */}
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <PrinterOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingInvoice ? '编辑发票' : '新增发票'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={600}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="company_name"
            label="公司名称"
            placeholder="请输入公司名称"
          />
          <ProFormText
            name="credit_code"
            label="纳税人识别号"
            placeholder="请输入纳税人识别号"
          />
          <ProFormDigit
            name="price"
            label="开票金额"
            placeholder="请输入开票金额"
            min={0}
            fieldProps={{ precision: 2 }}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormText
              name="contact_name"
              label="联系人"
              placeholder="请输入联系人"
            />
            <ProFormText
              name="contact_phone"
              label="联系电话"
              placeholder="请输入联系电话"
            />
          </div>
          <ProFormText
            name="address"
            label="邮寄地址"
            placeholder="请输入邮寄地址"
          />
          <ProFormSelect
            name="invoice_status"
            label="开票状态"
            placeholder="请选择开票状态"
            options={[
              { label: '未开票', value: 0 },
              { label: '已开票', value: 1 },
            ]}
          />
        </div>
      </ModalForm>
    </div>
  );
}
